import React, { useState, useEffect } from 'react';
import Dashboard from './Dashboard';
import { 
  User, Lock, ArrowRight, AlertTriangle, BarChart3, Building2, 
  Package, Briefcase, Settings, X, Globe, Laptop, ArrowUpRight, 
  Phone, Mail, MapPin, ExternalLink, Activity, HardHat, AlertCircle, 
  TrendingUp, CheckCircle, Database, ShieldAlert, Cpu,
  Facebook, Youtube, Instagram, Linkedin
} from 'lucide-react';
import axios from 'axios';
import logo from './logo.jpg';
import bgSlide from './bg_slide.png';
import logoDynamic from './logo_dynamic.gif';
import logoGroup from './logo_group.png';
import logoBuilders from './logo_builders.png';

// ==========================================
// 1. FINALIZED D&S LOGIN COMPONENT (UNCHANGED CORE LOGIC)
// ==========================================
function Login({ onLogin, onBackToPortal, isStandalone }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async () => {
    if (!username || !password) return;
    try {
      const res = await axios.post('/api/login', { username, password });
      if (res.data.success) {
        onLogin(res.data.user);
      }
    } catch (err) {
      if (!err.response) {
        setError('Connection Failed. Check black window.');
      } else {
        setError('Invalid Credentials');
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 relative overflow-hidden font-sans" style={{ background: 'radial-gradient(circle at 50% 0%, #002c44 0%, #001320 60%, #020b12 100%)' }}>
      {/* Sleek Grid Overlay & Ambient Glowing Orbs */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none -z-15"></div>
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-gradient-to-br from-bodla-gold/5 to-transparent blur-[120px] pointer-events-none -z-10 animate-pulse" style={{ animationDuration: '8s' }}></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-gradient-to-tr from-bodla-navyLight/15 to-transparent blur-[120px] pointer-events-none -z-10"></div>
      
      {/* Top Floating Action Bar */}
      {!isStandalone && (
        <div className="absolute top-6 left-6 z-20">
          <button 
            onClick={onBackToPortal}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-bodla-gold bg-bodla-navy/80 hover:bg-bodla-gold/10 border border-bodla-gold/20 rounded-xl transition-all shadow-gold-sm hover:-translate-x-0.5"
          >
            ← Return to Operations & Management Portal
          </button>
        </div>
      )}

      <div className="w-full max-w-md bg-bodla-navy/90 rounded-3xl border border-bodla-gold/20 shadow-gold-lg overflow-hidden backdrop-blur-md z-10">
        <div className="bg-bodla-navyDark/90 p-8 text-center border-b border-bodla-gold/10 flex flex-col items-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <div className="relative group bg-bodla-navy border border-bodla-gold/25 p-2 rounded-2xl shadow-gold-sm hover:scale-105 transition-transform duration-300">
              <img src={logoGroup} alt="Bodla Group Logo" className="h-12 w-auto object-contain rounded-lg" />
            </div>
            <div className="relative group bg-bodla-navy border border-bodla-gold/25 p-2 rounded-full shadow-gold-sm hover:scale-105 transition-transform duration-300">
              <img src={logoBuilders} alt="Bodla Builders Logo" className="h-12 w-12 object-contain rounded-full" />
            </div>
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-white font-outfit">Bodla Builders</h1>
          <p className="text-bodla-gold mt-1.5 text-[10px] font-bold tracking-wider uppercase">D&S Secure Dashboard Access</p>
        </div>

        <div className="p-8 space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-300 mb-2 uppercase tracking-wide">User Identity</label>
            <div className="relative">
              <User className="absolute left-3.5 top-3.5 text-slate-400" size={18} />
              <input
                type="text"
                className="w-full pl-11 pr-4 py-3 bg-bodla-navyDark border border-bodla-gold/20 rounded-xl text-slate-200 font-medium focus:ring-2 focus:ring-bodla-gold focus:border-transparent outline-none transition-all placeholder:text-slate-500"
                placeholder="Enter Username (e.g. Admin)"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError('');
                }}
              />
            </div>
          </div>

          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
            <label className="block text-xs font-bold text-slate-300 mb-2 uppercase tracking-wide">Enter Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full px-4 py-3 bg-bodla-navyDark border border-bodla-gold/20 rounded-xl text-slate-200 focus:ring-2 focus:ring-bodla-gold outline-none transition-all placeholder:text-slate-500"
              placeholder="••••••••"
            />
            {error && (
              <p className="text-rose-500 text-xs mt-2 font-medium flex items-center gap-1.5 animate-pulse">
                <AlertTriangle size={13} /> {error}
              </p>
            )}
          </div>

          <button
            onClick={handleLogin}
            disabled={!username || !password}
            className={`w-full mt-3 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all shadow-md ${
              username && password
                ? 'bg-gradient-to-r from-bodla-gold to-bodla-accent hover:from-bodla-goldHover hover:to-bodla-accent hover:shadow-gold-glow transform hover:-translate-y-0.5'
                : 'bg-slate-700/50 cursor-not-allowed text-slate-400 border border-slate-600/30'
            }`}
          >
            Access Dashboard <ArrowRight size={18} />
          </button>

          <p className="text-center text-slate-500 text-[10px] mt-6 tracking-widest uppercase">
            Authorized Personnel Only • v2.0.2
          </p>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// 2. STUNNING MAIN ROUTER & HOMEPAGE PORTAL
// ==========================================
export default function App() {
  const params = new URLSearchParams(window.location.search);
  const isStandalone = process.env.REACT_APP_STANDALONE_DS === 'true' || params.get('standalone') === 'true' || params.get('app') === 'ds';

  const [currentRoute, setCurrentRoute] = useState(isStandalone ? 'ds_app' : 'portal'); // 'portal', 'ds_app', 'redirect_screen'
  const [user, setUser] = useState(null); // D&S user session
  const [waStatus, setWaStatus] = useState('CONNECTING');
  const [time, setTime] = useState(new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [activeRedirectApp, setActiveRedirectApp] = useState(null);
  
  // Custom configured Dashboard URLs (with local and production fallbacks, saved in localStorage)
  const [dashboardUrls, setDashboardUrls] = useState({
    ds: 'in_app',
    dpr: 'https://bodlabuilders.online/dpr',
    inventory: 'https://bodlabuilders.online/inventory',
    erp: 'https://bodlabuilders.online/erp'
  });
  
  // Custom Local Port configurations for easy local developer testing
  const [localUrls, setLocalUrls] = useState({
    dpr: 'http://localhost:5173',
    inventory: 'http://localhost:5002',
    erp: 'http://localhost:6010'
  });
  
  // Mode selection: 'vps' (production subdomains) or 'local' (local development ports)
  const [envMode, setEnvMode] = useState('vps'); // 'vps' or 'local'

  // Load saved config on mount
  useEffect(() => {
    const savedUrls = localStorage.getItem('bodla_dashboard_urls');
    const savedLocal = localStorage.getItem('bodla_local_urls');
    const savedMode = localStorage.getItem('bodla_env_mode');
    
    if (savedUrls) setDashboardUrls(JSON.parse(savedUrls));
    if (savedLocal) setLocalUrls(JSON.parse(savedLocal));
    if (savedMode) setEnvMode(savedMode);
  }, []);

  // Update clock
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Poll WhatsApp / Server Connection Status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get('/api/whatsapp/status');
        setWaStatus(res.data.status || 'CONNECTED');
      } catch (err) {
        setWaStatus('DISCONNECTED');
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  // Save Settings Changes
  const saveConfig = () => {
    localStorage.setItem('bodla_dashboard_urls', JSON.stringify(dashboardUrls));
    localStorage.setItem('bodla_local_urls', JSON.stringify(localUrls));
    localStorage.setItem('bodla_env_mode', envMode);
    setShowSettings(false);
  };

  const handleLaunchDashboard = (appKey) => {
    if (appKey === 'ds') {
      setCurrentRoute('ds_app');
    } else {
      const targetUrl = envMode === 'vps' ? dashboardUrls[appKey] : localUrls[appKey];
      
      // We will show a premium redirection connection screen first (a highly corporate experience!)
      setActiveRedirectApp({
        key: appKey,
        name: getAppName(appKey),
        url: targetUrl,
        icon: getAppIcon(appKey),
        desc: getAppDesc(appKey)
      });
      setCurrentRoute('redirect_screen');
    }
  };

  const getAppName = (key) => {
    switch (key) {
      case 'ds': return 'D&S Dashboard';
      case 'dpr': return 'DPR Dashboard';
      case 'inventory': return 'Store Inventory Dashboard';
      case 'erp': return 'ERP Dashboard';
      default: return '';
    }
  };

  const getAppDesc = (key) => {
    switch (key) {
      case 'ds': return 'Demand & Supply analysis, procurement automation, and automated WhatsApp site alerts.';
      case 'dpr': return 'Daily Progress Reports tracking onsite concrete volume, brick masonry, finishing, and labor strengths.';
      case 'inventory': return 'Warehouse store stock receipts, material issuance, and automated low-stock warnings.';
      case 'erp': return 'Enterprise resource management mapping real-time finance cashflows, human resources, and budgets.';
      default: return '';
    }
  };

  const getAppIcon = (key, size = 32) => {
    switch (key) {
      case 'ds': return <BarChart3 className="text-bodla-gold" size={size} />;
      case 'dpr': return <Building2 className="text-bodla-gold" size={size} />;
      case 'inventory': return <Package className="text-bodla-gold" size={size} />;
      case 'erp': return <Briefcase className="text-bodla-gold" size={size} />;
      default: return null;
    }
  };

  // Render Redirection Confirmation Screen
  if (currentRoute === 'redirect_screen' && activeRedirectApp) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 relative overflow-hidden font-sans" style={{ background: 'radial-gradient(circle at 50% 0%, #002c44 0%, #001320 60%, #020b12 100%)' }}>
        {/* Sleek Grid Overlay & Ambient Glowing Orbs */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none -z-15"></div>
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-gradient-to-br from-bodla-gold/5 to-transparent blur-[120px] pointer-events-none -z-10 animate-pulse" style={{ animationDuration: '8s' }}></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-gradient-to-tr from-bodla-navyLight/15 to-transparent blur-[120px] pointer-events-none -z-10"></div>
        
        <div className="w-full max-w-xl bg-bodla-navy/90 rounded-3xl border border-bodla-gold/20 shadow-gold-lg overflow-hidden backdrop-blur-md p-8 text-center space-y-6 z-10">
          <div className="mx-auto w-24 h-24 bg-bodla-navy border border-bodla-gold/30 rounded-full flex items-center justify-center animate-bounce shadow-gold-sm">
            {activeRedirectApp.icon}
          </div>
          
          <div className="space-y-2">
            <h1 className="text-3xl font-extrabold tracking-tight text-white font-outfit">{activeRedirectApp.name}</h1>
            <p className="text-bodla-gold text-xs font-bold uppercase tracking-wider">Establishing Secure Connection Portal</p>
          </div>
          
          <p className="text-slate-300 text-sm max-w-md mx-auto leading-relaxed font-light">
            {activeRedirectApp.desc}
          </p>

          <div className="bg-bodla-navyDark/95 border border-bodla-gold/10 rounded-2xl p-4 max-w-md mx-auto text-left font-mono text-xs space-y-2 text-slate-400">
            <div className="flex justify-between border-b border-bodla-gold/5 pb-2">
              <span className="text-bodla-gold">Routing Address:</span>
              <span className="text-white truncate max-w-[200px]" title={activeRedirectApp.url}>{activeRedirectApp.url}</span>
            </div>
            <div className="flex justify-between border-b border-bodla-gold/5 pb-2">
              <span className="text-bodla-gold">Target Environment:</span>
              <span className="text-emerald-400 font-bold uppercase">{envMode === 'vps' ? 'VPS Subdomain' : 'Local Host'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-bodla-gold">SSL Tunnel State:</span>
              <span className="text-emerald-400 animate-pulse">SECURE/ACTIVE</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <button 
              onClick={() => setCurrentRoute('portal')}
              className="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-slate-300 bg-bodla-navyDark hover:bg-bodla-navy border border-bodla-gold/20 hover:text-white transition-all text-sm"
            >
              Cancel & Go Back
            </button>
            <a 
              href={activeRedirectApp.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setCurrentRoute('portal')}
              className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-bodla-gold to-bodla-accent hover:from-bodla-goldHover hover:to-bodla-accent hover:shadow-gold-glow flex items-center justify-center gap-2 transform hover:-translate-y-0.5 transition-all text-sm"
            >
              Launch Dashboard <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Render Inner D&S Dashboard App
  if (currentRoute === 'ds_app') {
    if (!user) {
      return <Login onLogin={setUser} onBackToPortal={() => setCurrentRoute('portal')} isStandalone={isStandalone} />;
    }
    return (
      <div className="min-h-screen bg-bodla-navyDark relative">
        {/* Render Finalized Dashboard component */}
        <Dashboard 
          user={user} 
          onLogout={() => setUser(null)} 
          onBackToPortal={!isStandalone ? () => setCurrentRoute('portal') : null} 
        />
      </div>
    );
  }

  // Render Executive Homepage Portal
  return (
    <div 
      className="min-h-screen text-slate-200 flex flex-col font-sans relative overflow-hidden select-none pb-12" 
      style={{ 
        background: 'radial-gradient(circle at 50% 0%, #002c44 0%, #001320 60%, #020b12 100%)',
      }}
    >
      {/* Sleek Grid Overlay & Ambient Glowing Orbs */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none -z-15"></div>
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-gradient-to-br from-bodla-gold/5 to-transparent blur-[120px] pointer-events-none -z-10 animate-pulse" style={{ animationDuration: '8s' }}></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-gradient-to-tr from-bodla-navyLight/15 to-transparent blur-[120px] pointer-events-none -z-10"></div>
      
      {/* ==========================================
          HEADER SECTION (DYNAMIC DUAL LOGOS - GROUP ON LEFT, BUILDERS ON RIGHT)
         ========================================== */}
      <header className="bg-bodla-navyDark/65 border-b border-bodla-gold/15 backdrop-blur-md px-6 py-4 flex items-center justify-between z-30 sticky top-0">
        {/* Header Left: Bodla Group Logo & Branding */}
        <div className="flex items-center gap-4">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-bodla-gold to-bodla-accent rounded-xl blur opacity-25 group-hover:opacity-60 transition duration-500"></div>
            <img 
              src={logoGroup} 
              alt="Bodla Group Logo" 
              className="relative h-12 w-auto object-contain rounded-xl border border-bodla-gold/20 bg-white/5 px-2 py-1 shadow-gold-sm hover:scale-[1.03] transition-transform duration-300"
            />
          </div>
          <div className="pl-3 border-l border-bodla-gold/30">
            <h1 className="text-xl md:text-2xl font-black tracking-widest text-white leading-tight font-outfit uppercase">
              BODLA GROUP
            </h1>
          </div>
        </div>

        {/* Header Right: Bodla Builders Logo, Status, Clock, Settings */}
        <div className="flex items-center gap-4 md:gap-6">
          {/* Active status bubble */}
          <div className="hidden lg:flex items-center gap-2.5 px-4 py-2 bg-bodla-navyDark/85 border border-bodla-gold/20 rounded-full text-xs">
            <span className={`w-2 h-2 rounded-full gold-dot-pulse ${
              waStatus === 'CONNECTED' ? 'bg-emerald-500' : 'bg-amber-400'
            }`}></span>
            <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">
              Server Gate: {waStatus === 'CONNECTED' ? 'Online' : 'Standby'}
            </span>
          </div>

          {/* Real-time Clock */}
          <div className="hidden md:block text-right border-r border-bodla-gold/10 pr-5">
            <p className="text-sm font-black text-white font-mono tracking-wider">
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
              {time.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Bodla Builders Pvt Ltd Logo (on the right!) */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-[10px] font-black text-white uppercase tracking-wider font-outfit">BODLA BUILDERS</p>
              <p className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">(PVT) LTD.</p>
            </div>
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-bodla-gold to-bodla-accent rounded-full blur opacity-20 group-hover:opacity-50 transition duration-500"></div>
              <img 
                src={logoBuilders} 
                alt="Bodla Builders Logo" 
                className="relative h-11 w-11 object-contain rounded-full border border-bodla-gold/20 bg-bodla-navyDark/90 p-1.5 shadow-gold-sm hover:scale-[1.03] transition-transform duration-300"
              />
            </div>
          </div>

          {/* Settings Trigger */}
          <button 
            onClick={() => setShowSettings(true)}
            className="p-3 bg-bodla-navyDark/90 hover:bg-bodla-gold/15 border border-bodla-gold/25 text-bodla-gold rounded-xl transition-all shadow-gold hover:scale-105 active:scale-95 group"
            title="Gateway Configuration Settings"
          >
            <Settings size={18} className="group-hover:rotate-90 transition-transform duration-700" />
          </button>
        </div>
      </header>

      {/* ==========================================
          HERO & PORTAL CONTENT
         ========================================== */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8 flex flex-col justify-center space-y-10 z-10 font-sans">
        
        {/* Welcome and Corporate Statement */}
        <div className="text-center space-y-4 max-w-3xl mx-auto animate-fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-bodla-gold/10 border border-bodla-gold/35 rounded-full text-[10px] font-black uppercase text-bodla-gold tracking-widest shadow-gold-sm">
            <Cpu size={12} className="animate-pulse" /> Enterprise Operations & Management Portal v2.0.2
          </div>
          <h2 className="text-4xl md:text-5.5xl font-black text-white tracking-tight leading-none font-outfit">
            Welcome to <span className="text-gold-gradient drop-shadow">Operations & Management Portal</span>
          </h2>
          <p className="text-slate-300 text-xs md:text-sm leading-relaxed max-w-2xl mx-auto drop-shadow font-light">
            Centralizing real-time analytics, daily construction logs, supply-chain workflows, and resource allocation protocols across all major **Bodla Group** infrastructure projects.
          </p>
        </div>

        {/* ==========================================
            EXECUTIVE CONTROL SUMMARY STRIP (PREMIUM KPI METRICS)
           ========================================== */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl w-full mx-auto bg-bodla-navy/70 border border-bodla-gold/15 rounded-2xl p-4 backdrop-blur-md shadow-gold-sm">
          <div className="flex items-center gap-3.5 p-2 border-r border-slate-800/60 last:border-0">
            <div className="p-2 bg-bodla-gold/10 rounded-lg text-bodla-gold shadow-gold-sm">
              <Building2 size={16} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Sites</p>
              <h4 className="text-sm font-bold text-white font-outfit mt-0.5">4 Projects Live</h4>
            </div>
          </div>
          
          <div className="flex items-center gap-3.5 p-2 border-r border-slate-800/60 last:border-0">
            <div className="p-2 bg-bodla-gold/10 rounded-lg text-bodla-gold shadow-gold-sm">
              <BarChart3 size={16} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Demands</p>
              <h4 className="text-sm font-bold text-white font-outfit mt-0.5">1,280+ Records</h4>
            </div>
          </div>

          <div className="flex items-center gap-3.5 p-2 border-r border-slate-800/60 last:border-0">
            <div className="p-2 bg-bodla-gold/10 rounded-lg text-bodla-gold shadow-gold-sm">
              <Phone size={16} className="animate-pulse" />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">WhatsApp Alerts</p>
              <h4 className="text-sm font-bold text-emerald-400 font-outfit mt-0.5">Sync Active</h4>
            </div>
          </div>

          <div className="flex items-center gap-3.5 p-2 last:border-0">
            <div className="p-2 bg-bodla-gold/10 rounded-lg text-bodla-gold shadow-gold-sm">
              <Activity size={16} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Health</p>
              <h4 className="text-sm font-bold text-emerald-400 font-outfit mt-0.5">99.98% Uptime</h4>
            </div>
          </div>
        </div>

        {/* ==========================================
            MAGNIFICENT 4 CARD GRID WITH LIVE MINI PREVIEW WIDGETS
           ========================================== */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-2 animate-slide-up">
          
          {/* Card 1: D&S Dashboard */}
          <div className="glass-panel hover:glass-panel-heavy rounded-3xl p-5 flex flex-col justify-between transition-all duration-300 transform hover:-translate-y-2.5 gold-border-glow shadow-gold-sm relative group overflow-hidden border border-bodla-gold/10">
            {/* Soft decorative glow background */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-bodla-gold/5 rounded-full blur-2xl group-hover:bg-bodla-gold/10 transition-all duration-300"></div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 bg-bodla-gold/10 border border-bodla-gold/20 rounded-xl flex items-center justify-center shadow-gold-sm">
                  <BarChart3 className="text-bodla-gold" size={22} />
                </div>
                <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-[8px] font-extrabold uppercase rounded-full tracking-wider">Local/SPA</span>
              </div>
              
              <div className="space-y-1">
                <h3 className="text-lg font-black text-white tracking-wide font-outfit">D&S Dashboard</h3>
                <p className="text-bodla-gold text-[10px] font-bold uppercase tracking-wider">Demand & Supply Sync</p>
              </div>
              
              <p className="text-slate-400 text-[11px] leading-relaxed font-light">
                Consolidates material orders from construction managers, tracks delivery progress, and dispatches automated WhatsApp group reports.
              </p>

            </div>
            
            <div className="pt-4">
              <button 
                onClick={() => handleLaunchDashboard('ds')}
                className="w-full py-2.5 bg-gradient-to-r from-bodla-gold to-bodla-accent hover:from-bodla-goldHover hover:to-bodla-accent text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5"
              >
                Access Portal <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Card 2: DPR Dashboard */}
          <div className="glass-panel hover:glass-panel-heavy rounded-3xl p-5 flex flex-col justify-between transition-all duration-300 transform hover:-translate-y-2.5 gold-border-glow shadow-gold-sm relative group overflow-hidden border border-bodla-gold/10">
            <div className="absolute top-0 right-0 w-24 h-24 bg-bodla-gold/5 rounded-full blur-2xl group-hover:bg-bodla-gold/10 transition-all duration-300"></div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 bg-bodla-gold/10 border border-bodla-gold/20 rounded-xl flex items-center justify-center shadow-gold-sm">
                  <Building2 className="text-bodla-gold" size={22} />
                </div>
                <span className="px-2 py-0.5 bg-sky-500/10 border border-sky-500/25 text-sky-400 text-[8px] font-extrabold uppercase rounded-full tracking-wider">Subdomain</span>
              </div>
              
              <div className="space-y-1">
                <h3 className="text-lg font-black text-white tracking-wide font-outfit">DPR Dashboard</h3>
                <p className="text-bodla-gold text-[10px] font-bold uppercase tracking-wider">Daily Progress Logs</p>
              </div>
              
              <p className="text-slate-400 text-[11px] leading-relaxed font-light">
                Aggregates active work progress reports, weather states, brick laying counts, masonry rates, and contractor performance logs.
              </p>

            </div>
            
            <div className="pt-4">
              <button 
                onClick={() => handleLaunchDashboard('dpr')}
                className="w-full py-2.5 bg-gradient-to-r from-bodla-gold to-bodla-accent hover:from-bodla-goldHover hover:to-bodla-accent text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5"
              >
                Access Portal <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Card 3: Store Inventory */}
          <div className="glass-panel hover:glass-panel-heavy rounded-3xl p-5 flex flex-col justify-between transition-all duration-300 transform hover:-translate-y-2.5 gold-border-glow shadow-gold-sm relative group overflow-hidden border border-bodla-gold/10">
            <div className="absolute top-0 right-0 w-24 h-24 bg-bodla-gold/5 rounded-full blur-2xl group-hover:bg-bodla-gold/10 transition-all duration-300"></div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 bg-bodla-gold/10 border border-bodla-gold/20 rounded-xl flex items-center justify-center shadow-gold-sm">
                  <Package className="text-bodla-gold" size={22} />
                </div>
                <span className="px-2 py-0.5 bg-sky-500/10 border border-sky-500/25 text-sky-400 text-[8px] font-extrabold uppercase rounded-full tracking-wider">Subdomain</span>
              </div>
              
              <div className="space-y-1">
                <h3 className="text-lg font-black text-white tracking-wide font-outfit">Store Inventory</h3>
                <p className="text-bodla-gold text-[10px] font-bold uppercase tracking-wider">Storage Analytics</p>
              </div>
              
              <p className="text-slate-400 text-[11px] leading-relaxed font-light">
                Controls central warehouse supplies, traces material issuance receipts to contractors, and sets up automated stock replenishment flags.
              </p>

            </div>
            
            <div className="pt-4">
              <button 
                onClick={() => handleLaunchDashboard('inventory')}
                className="w-full py-2.5 bg-gradient-to-r from-bodla-gold to-bodla-accent hover:from-bodla-goldHover hover:to-bodla-accent text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5"
              >
                Access Portal <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Card 4: ERP Dashboard */}
          <div className="glass-panel hover:glass-panel-heavy rounded-3xl p-5 flex flex-col justify-between transition-all duration-300 transform hover:-translate-y-2.5 gold-border-glow shadow-gold-sm relative group overflow-hidden border border-bodla-gold/10">
            <div className="absolute top-0 right-0 w-24 h-24 bg-bodla-gold/5 rounded-full blur-2xl group-hover:bg-bodla-gold/10 transition-all duration-300"></div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 bg-bodla-gold/10 border border-bodla-gold/20 rounded-xl flex items-center justify-center shadow-gold-sm">
                  <Briefcase className="text-bodla-gold" size={22} />
                </div>
                <span className="px-2 py-0.5 bg-sky-500/10 border border-sky-500/25 text-sky-400 text-[8px] font-extrabold uppercase rounded-full tracking-wider">Subdomain</span>
              </div>
              
              <div className="space-y-1">
                <h3 className="text-lg font-black text-white tracking-wide font-outfit">ERP Dashboard</h3>
                <p className="text-bodla-gold text-[10px] font-bold uppercase tracking-wider">Enterprise Accounts</p>
              </div>
              
              <p className="text-slate-400 text-[11px] leading-relaxed font-light">
                Manages complete company books, active cashier flows, salaries payrolls, contractor ledger vouchers, and CEO administrative approvals.
              </p>

            </div>
            
            <div className="pt-4">
              <button 
                onClick={() => handleLaunchDashboard('erp')}
                className="w-full py-2.5 bg-gradient-to-r from-bodla-gold to-bodla-accent hover:from-bodla-goldHover hover:to-bodla-accent text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5"
              >
                Access Portal <ArrowRight size={14} />
              </button>
            </div>
          </div>

        </div>
      </main>

      {/* ==========================================
          FLOATING SOCIAL MEDIA DOCK (SMART & PREMIUM)
         ========================================== */}
      <div className="fixed right-5 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-40 bg-bodla-navy/70 border border-bodla-gold/25 p-2.5 rounded-2xl backdrop-blur-md shadow-gold-sm transition-all duration-300 hover:border-bodla-gold/50">
        <a 
          href="https://www.facebook.com/bbdha" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="group/item relative flex items-center justify-center p-2.5 rounded-xl bg-bodla-navyDark/80 hover:bg-bodla-gold hover:text-bodla-navy text-bodla-gold border border-bodla-gold/10 hover:border-transparent hover:scale-110 active:scale-95 transition-all duration-300 shadow-sm"
        >
          <Facebook size={18} />
          <span className="absolute right-12 scale-0 group-hover/item:scale-100 bg-bodla-navyDark border border-bodla-gold/30 text-white text-[10px] font-bold uppercase tracking-wider py-1 px-3.5 rounded-lg shadow-gold-sm transition-all duration-200 origin-right whitespace-nowrap pointer-events-none">
            Facebook
          </span>
        </a>
        
        <a 
          href="https://www.youtube.com/@BodlaBuilders" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="group/item relative flex items-center justify-center p-2.5 rounded-xl bg-bodla-navyDark/80 hover:bg-bodla-gold hover:text-bodla-navy text-bodla-gold border border-bodla-gold/10 hover:border-transparent hover:scale-110 active:scale-95 transition-all duration-300 shadow-sm"
        >
          <Youtube size={18} />
          <span className="absolute right-12 scale-0 group-hover/item:scale-100 bg-bodla-navyDark border border-bodla-gold/30 text-white text-[10px] font-bold uppercase tracking-wider py-1 px-3.5 rounded-lg shadow-gold-sm transition-all duration-200 origin-right whitespace-nowrap pointer-events-none">
            YouTube
          </span>
        </a>

        <a 
          href="https://www.instagram.com/bodlabuilders_" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="group/item relative flex items-center justify-center p-2.5 rounded-xl bg-bodla-navyDark/80 hover:bg-bodla-gold hover:text-bodla-navy text-bodla-gold border border-bodla-gold/10 hover:border-transparent hover:scale-110 active:scale-95 transition-all duration-300 shadow-sm"
        >
          <Instagram size={18} />
          <span className="absolute right-12 scale-0 group-hover/item:scale-100 bg-bodla-navyDark border border-bodla-gold/30 text-white text-[10px] font-bold uppercase tracking-wider py-1 px-3.5 rounded-lg shadow-gold-sm transition-all duration-200 origin-right whitespace-nowrap pointer-events-none">
            Instagram
          </span>
        </a>

        <a 
          href="https://www.linkedin.com/company/bodla-group/" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="group/item relative flex items-center justify-center p-2.5 rounded-xl bg-bodla-navyDark/80 hover:bg-bodla-gold hover:text-bodla-navy text-bodla-gold border border-bodla-gold/10 hover:border-transparent hover:scale-110 active:scale-95 transition-all duration-300 shadow-sm"
        >
          <Linkedin size={18} />
          <span className="absolute right-12 scale-0 group-hover/item:scale-100 bg-bodla-navyDark border border-bodla-gold/30 text-white text-[10px] font-bold uppercase tracking-wider py-1 px-3.5 rounded-lg shadow-gold-sm transition-all duration-200 origin-right whitespace-nowrap pointer-events-none">
            LinkedIn
          </span>
        </a>
      </div>

      {/* ==========================================
          PROFESSIONAL EXECUTIVE CORPORATE FOOTER
         ========================================== */}
      <footer className="w-full max-w-7xl mx-auto px-6 pt-10 border-t border-bodla-gold/15 mt-12 bg-bodla-navy/30 backdrop-blur-md rounded-t-3xl z-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 pb-10">
          {/* Column 1: Brand Stack & Address */}
          <div className="space-y-4 col-span-1 md:col-span-1">
            <div className="flex items-center gap-3">
              <img src={logoGroup} alt="Bodla Group Logo" className="h-9 w-auto object-contain rounded-lg bg-white/5 p-1 border border-bodla-gold/10" />
              <div>
                <h4 className="text-sm font-black text-white tracking-widest font-outfit uppercase">BODLA GROUP</h4>
                <p className="text-[9px] text-bodla-gold tracking-widest font-extrabold uppercase">Central Gateway</p>
              </div>
            </div>
            <p className="text-slate-400 text-xs font-light leading-relaxed">
              A leading infrastructure and development enterprise, committed to building iconic landmarks and delivering sustainable modern housing structures across Pakistan.
            </p>
            <div className="flex items-start gap-2.5 text-xs text-slate-300">
              <MapPin size={16} className="text-bodla-gold shrink-0 mt-0.5" />
              <span className="leading-relaxed font-light">
                Business Hub, Central Square, Sector K, Phase I, DHA Multan, Multan, Pakistan.
              </span>
            </div>
          </div>

          {/* Column 2: Quick Navigation */}
          <div className="space-y-4">
            <h5 className="text-xs font-black text-white uppercase tracking-widest border-b border-bodla-gold/15 pb-2">Unified Dashboards</h5>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button onClick={() => handleLaunchDashboard('ds')} className="text-slate-400 hover:text-bodla-gold flex items-center gap-1.5 transition-colors">
                  <ArrowUpRight size={12} /> Demand & Supply Portal
                </button>
              </li>
              <li>
                <button onClick={() => handleLaunchDashboard('dpr')} className="text-slate-400 hover:text-bodla-gold flex items-center gap-1.5 transition-colors">
                  <ArrowUpRight size={12} /> Daily Progress Reports
                </button>
              </li>
              <li>
                <button onClick={() => handleLaunchDashboard('inventory')} className="text-slate-400 hover:text-bodla-gold flex items-center gap-1.5 transition-colors">
                  <ArrowUpRight size={12} /> Store Stock Inventory
                </button>
              </li>
              <li>
                <button onClick={() => handleLaunchDashboard('erp')} className="text-slate-400 hover:text-bodla-gold flex items-center gap-1.5 transition-colors">
                  <ArrowUpRight size={12} /> Enterprise Accounts ERP
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Corporate Directory */}
          <div className="space-y-4">
            <h5 className="text-xs font-black text-white uppercase tracking-widest border-b border-bodla-gold/15 pb-2">Support Desk</h5>
            <ul className="space-y-3 text-xs">
              <li className="flex items-center gap-2.5 text-slate-300">
                <Phone size={14} className="text-bodla-gold shrink-0" />
                <span className="font-mono font-medium">061-111-126-352</span>
              </li>
              <li className="flex items-center gap-2.5 text-slate-300">
                <Mail size={14} className="text-bodla-gold shrink-0" />
                <a href="mailto:info@bodlagroup.com" className="hover:text-bodla-gold transition-colors font-medium">info@bodlagroup.com</a>
              </li>
              <li className="flex items-center gap-2.5 text-slate-300">
                <Globe size={14} className="text-bodla-gold shrink-0" />
                <a href="https://www.bodlagroup.com" target="_blank" rel="noopener noreferrer" className="hover:text-bodla-gold transition-colors font-medium flex items-center gap-1">
                  www.bodlagroup.com <ExternalLink size={10} />
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Social Connect */}
          <div className="space-y-4">
            <h5 className="text-xs font-black text-white uppercase tracking-widest border-b border-bodla-gold/15 pb-2">Social Connect</h5>
            <p className="text-slate-400 text-xs font-light leading-relaxed">
              Stay connected with our latest project developments and corporate announcements.
            </p>
            <div className="flex gap-2">
              <a href="https://www.facebook.com/bbdha" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-bodla-navyDark border border-bodla-gold/15 text-bodla-gold hover:bg-bodla-gold hover:text-bodla-navy hover:scale-105 active:scale-95 transition-all duration-300 shadow-sm" title="Facebook">
                <Facebook size={16} />
              </a>
              <a href="https://www.youtube.com/@BodlaBuilders" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-bodla-navyDark border border-bodla-gold/15 text-bodla-gold hover:bg-bodla-gold hover:text-bodla-navy hover:scale-105 active:scale-95 transition-all duration-300 shadow-sm" title="YouTube">
                <Youtube size={16} />
              </a>
              <a href="https://www.instagram.com/bodlabuilders_" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-bodla-navyDark border border-bodla-gold/15 text-bodla-gold hover:bg-bodla-gold hover:text-bodla-navy hover:scale-105 active:scale-95 transition-all duration-300 shadow-sm" title="Instagram">
                <Instagram size={16} />
              </a>
              <a href="https://www.linkedin.com/company/bodla-group/" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-bodla-navyDark border border-bodla-gold/15 text-bodla-gold hover:bg-bodla-gold hover:text-bodla-navy hover:scale-105 active:scale-95 transition-all duration-300 shadow-sm" title="LinkedIn">
                <Linkedin size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Rights Bar */}
        <div className="border-t border-slate-800/80 py-6 text-center space-y-2">
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">
            Copyright © {new Date().getFullYear()} Bodla Group. All Rights Reserved.
          </p>
          <p className="text-[8px] text-slate-500 font-bold uppercase tracking-wider flex items-center justify-center gap-1.5">
            <span>Central Management Gateway v2.0.2</span>
            <span>•</span>
            <span className="text-emerald-400 flex items-center gap-0.5"><CheckCircle size={8} /> Secure SSL Connected</span>
          </p>
        </div>
      </footer>

      {/* ==========================================
          DYNAMIC SETTINGS MODAL
         ========================================== */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bodla-bgDark/80 backdrop-blur-sm select-none font-sans">
          <div className="w-full max-w-lg bg-bodla-navy border border-bodla-gold/30 rounded-3xl shadow-gold-lg overflow-hidden animate-scale-in">
            
            <div className="bg-bodla-navyDark px-6 py-4 flex items-center justify-between border-b border-bodla-gold/15">
              <div className="flex items-center gap-2 text-bodla-gold">
                <Settings size={20} className="animate-spin-slow" />
                <h3 className="font-extrabold tracking-wide uppercase text-white text-sm font-outfit">Gateway Settings</h3>
              </div>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              
              {/* Environment toggle selector */}
              <div className="space-y-2">
                <label className="block text-xs font-black text-slate-300 uppercase tracking-wide">Environment Selection</label>
                <div className="grid grid-cols-2 gap-2 bg-bodla-navyDark border border-bodla-gold/10 p-1.5 rounded-xl">
                  <button 
                    onClick={() => setEnvMode('vps')}
                    className={`py-2 px-4 rounded-lg font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all ${
                      envMode === 'vps' 
                        ? 'bg-gradient-to-r from-bodla-gold to-bodla-accent text-white shadow-md' 
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
                    }`}
                  >
                    <Globe size={13} /> VPS Subdomains
                  </button>
                  <button 
                    onClick={() => setEnvMode('local')}
                    className={`py-2 px-4 rounded-lg font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all ${
                      envMode === 'local' 
                        ? 'bg-gradient-to-r from-bodla-gold to-bodla-accent text-white shadow-md' 
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
                    }`}
                  >
                    <Laptop size={13} /> Local Ports
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 leading-normal">
                  Toggle between live VPS domains (e.g. <i>dpr.bodla.online</i>) and local development testing ports (e.g. <i>localhost:5173</i>) dynamically.
                </p>
              </div>

              {/* URL Fields Section */}
              <div className="space-y-4 border-t border-slate-800 pt-4">
                <h4 className="text-xs font-black text-slate-200 uppercase tracking-wider flex items-center gap-1">
                  Configure Redirection Targets
                </h4>
                
                {envMode === 'vps' ? (
                  /* VPS Configuration List */
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">DPR Dashboard Subdomain</label>
                      </div>
                      <input 
                        type="text" 
                        value={dashboardUrls.dpr} 
                        onChange={(e) => setDashboardUrls({...dashboardUrls, dpr: e.target.value})}
                        className="w-full bg-bodla-navyDark border border-bodla-gold/15 focus:border-bodla-gold rounded-xl px-3 py-2.5 text-xs text-white outline-none font-mono"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Store Inventory Subdomain</label>
                      </div>
                      <input 
                        type="text" 
                        value={dashboardUrls.inventory} 
                        onChange={(e) => setDashboardUrls({...dashboardUrls, inventory: e.target.value})}
                        className="w-full bg-bodla-navyDark border border-bodla-gold/15 focus:border-bodla-gold rounded-xl px-3 py-2.5 text-xs text-white outline-none font-mono"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">ERP Dashboard Subdomain</label>
                      </div>
                      <input 
                        type="text" 
                        value={dashboardUrls.erp} 
                        onChange={(e) => setDashboardUrls({...dashboardUrls, erp: e.target.value})}
                        className="w-full bg-bodla-navyDark border border-bodla-gold/15 focus:border-bodla-gold rounded-xl px-3 py-2.5 text-xs text-white outline-none font-mono"
                      />
                    </div>
                  </div>
                ) : (
                  /* Local Configuration List */
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">DPR Local Port Link</label>
                      </div>
                      <input 
                        type="text" 
                        value={localUrls.dpr} 
                        onChange={(e) => setLocalUrls({...localUrls, dpr: e.target.value})}
                        className="w-full bg-bodla-navyDark border border-bodla-gold/15 focus:border-bodla-gold rounded-xl px-3 py-2.5 text-xs text-white outline-none font-mono"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">Store Inventory Local Port Link</label>
                      </div>
                      <input 
                        type="text" 
                        value={localUrls.inventory} 
                        onChange={(e) => setLocalUrls({...localUrls, inventory: e.target.value})}
                        className="w-full bg-bodla-navyDark border border-bodla-gold/15 focus:border-bodla-gold rounded-xl px-3 py-2.5 text-xs text-white outline-none font-mono"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase">ERP Local Port Link</label>
                      </div>
                      <input 
                        type="text" 
                        value={localUrls.erp} 
                        onChange={(e) => setLocalUrls({...localUrls, erp: e.target.value})}
                        className="w-full bg-bodla-navyDark border border-bodla-gold/15 focus:border-bodla-gold rounded-xl px-3 py-2.5 text-xs text-white outline-none font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>

            </div>

            <div className="bg-bodla-navyDark px-6 py-4 border-t border-bodla-gold/15 flex items-center justify-end gap-2.5">
              <button 
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500 rounded-xl text-xs font-bold uppercase transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={saveConfig}
                className="px-5 py-2 bg-gradient-to-r from-bodla-gold to-bodla-accent hover:from-bodla-goldHover hover:to-bodla-accent text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-md transform hover:-translate-y-0.5"
              >
                Save Changes
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
